import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./ApplicationSubmissionSection.scss";
import useAuth from "../../helper/useAuth";
import { PartnerApi } from "../../api/partnerApi";
import { getClientToken } from "../../helper/clientAuth";
import { useProfileQuery } from "../../features/auth/authApi";

const assets = "/assets/images/icons/";

const findProductBySlug = (value, slug) => {
    if (!value) return null;
    if (Array.isArray(value)) {
        return value.map((item) => findProductBySlug(item, slug)).find(Boolean) || null;
    }
    if (typeof value !== "object") return null;
    if (value.slug === slug) return value;
    return Object.values(value).map((item) => findProductBySlug(item, slug)).find(Boolean) || null;
};

const extractApplications = (data) => {
    const list = data?.data?.applications
        || data?.applications
        || data?.data?.items
        || (Array.isArray(data?.data) ? data.data : null)
        || (Array.isArray(data) ? data : null);
    return Array.isArray(list) ? list : [];
};

// The latest application decides the form state:
// rejected -> form is unlocked and status_note is shown, anything else -> form is locked.
const getApplicationState = (applications) => {
    if (!applications.length) return { locked: false, rejectionNote: "" };
    const time = (item) => new Date(item.updated_at || item.created_at || 0).getTime() || 0;
    const latest = [...applications].sort((a, b) => (time(b) - time(a)) || ((b.id || 0) - (a.id || 0)))[0];
    if (latest?.status === "rejected") {
        return { locked: false, rejectionNote: latest.status_note || "" };
    }
    return { locked: true, rejectionNote: "" };
};

async function createSharedLogin(email) {
    const response = await PartnerApi.post("/api/v1/auth/shared-login", {
        source: "payways",
        token: getClientToken(),
        email,
        product_slug: "payways",
        remember_me: false,
    });

    const accessToken = response.data?.data?.tokens?.access_token;
    if (!accessToken) throw new Error("Shared login did not return an access token");
    return accessToken;
}

function ApplicationForm({ accessToken, productId, contract, rejectionNote = "", onSuccess, disabled = false }) {
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dragging, setDragging] = useState(false);
    const copy = (key) => t(`partnership.application.${key}`);

    const addFiles = (selected) => {
        if (disabled) return;
        const incoming = Array.from(selected);
        const invalid = incoming.find((file) => !/\.(pdf|doc|docx)$/i.test(file.name) || file.size > 50 * 1024 * 1024);
        if (invalid) {
            setError(t(`partnership.form.errors.${invalid.size > 50 * 1024 * 1024 ? "signSize" : "signType"}`));
            return;
        }
        setError("");
        setFiles((previous) => [...previous, ...incoming.filter((file) => !previous.some((item) => item.name === file.name && item.size === file.size))]);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (disabled) return;
        setError("");
        setIsSubmitting(true);

        try {
            if (!accessToken) throw new Error("Shared login did not return an access token");
            const address = new FormData(form).get("address");

            await PartnerApi.patch(
                "/api/v1/me",
                { country: "Հայաստան", address },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            if (!productId) throw new Error("Application product was not found");
            const applicationResponse = await PartnerApi.post(
                "/api/v1/applications",
                { product_id: productId },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            const applicationId = applicationResponse.data?.data?.id
                || applicationResponse.data?.id
                || applicationResponse.data?.data?.application?.id;
            if (!applicationId) throw new Error("Application id was not returned");

            // The contract fetched from the product is attached automatically
            // (the "Signed contract" upload field is hidden for now).
            if (!contract?.download_url) throw new Error("Contract was not found");
            const contractResponse = await PartnerApi.get(contract.download_url, {
                headers: { Authorization: `Bearer ${accessToken}` },
                responseType: "blob",
            });
            const contractFile = new File(
                [contractResponse.data],
                contract.file_name || "payways-contract",
                { type: contractResponse.data?.type || "application/octet-stream" }
            );

            for (const file of [contractFile, ...files]) {
                const formData = new FormData();
                formData.append("file", file);
                await PartnerApi.post(`/api/v1/applications/${applicationId}/attachments`, formData, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "multipart/form-data",
                    },
                });
            }

            await PartnerApi.post(`/api/v1/applications/${applicationId}/transition`, { status: "submitted" }, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            form.reset();
            setFiles([]);
            onSuccess();
        } catch (submitError) {
            setError(submitError.response?.data?.message || submitError.message || t("partnership.form.errors.submit"));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form className="partnership-application__form" onSubmit={handleSubmit}>
            <header className="partnership-application__form-header">
                <h3>{t("partnership.step2.title")}</h3>
                <p>{t("partnership.step2.hint")}</p>
            </header>
            {rejectionNote && !disabled && (
                <div className="partnership-application__rejection" role="alert">
                    <strong>{t("partnership.form.rejectedTitle", "Ձեր հայտը մերժվել է")}</strong>
                    <p>{rejectionNote}</p>
                </div>
            )}
            <input type="hidden" name="applicantType" value="Physical" />
            <div className="partnership-application__fields">
                <input type="hidden" name="country" value="AM" />
                <label className="partnership-application__field">
                    <span>{t("partnership.form.addressLabel")} *<small>{copy("address")}</small></span>
                    <input name="address" autoComplete="street-address" placeholder={copy("address")} required disabled={disabled} />
                </label>
            </div>
            {/* «Ստորագրված պայմանագիր» դաշտը ժամանակավորապես թաքցված է — պայմանագիրն ուղարկվում է ավտոմատ
            <div className="partnership-application__upload">
                <label htmlFor="partnership-contract-files">{t("partnership.form.signLabel")}<span className="partnership-application__desktop"> *</span></label>
                <p className="partnership-application__multiple">{copy("multipleFiles")}</p>
                <label className={`partnership-application__dropzone${dragging ? " is-dragging" : ""}${disabled ? " is-disabled" : ""}`}
                    onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(event.dataTransfer.files); }}>
                    <input id="partnership-contract-files" type="file" accept=".pdf,.doc,.docx" multiple disabled={disabled} onChange={(event) => { addFiles(event.target.files); event.target.value = ""; }} aria-describedby={error ? "partnership-file-error" : undefined} />
                    <strong className="partnership-application__desktop">{copy("dropFiles")}</strong>
                    <strong className="partnership-application__compact">{t("partnership.form.signChoose")}</strong>
                    <span className="partnership-application__desktop">{copy("browseFiles")}</span>
                    <span className="partnership-application__compact">{copy("formats")}</span>
                </label>
                {error && <p id="partnership-file-error" className="partnership-application__error" role="alert">{error}</p>}
                {files.length > 0 && <ul className="partnership-application__files">
                    {files.map((file, index) => <li key={`${file.name}-${file.size}`}>
                        {/\.pdf$/i.test(file.name) && <img src={`${assets}file-pdf.svg`} alt="" />}
                        <span><strong>{file.name}</strong><small>{(file.size / (1024 * 1024)).toFixed(1)} MB</small></span>
                        <button type="button" disabled={disabled} aria-label={`${t("partnership.form.signRemove")}: ${file.name}`} onClick={() => setFiles((previous) => previous.filter((_, i) => i !== index))}><img src={`${assets}trash.svg`} alt="" /></button>
                    </li>)}
                </ul>}
            </div>
            */}
            {error && <p className="partnership-application__error" role="alert">{error}</p>}
            {/* Presentation only: submission will be connected with the application API. */}
            {disabled && <p className="partnership-application__success" role="status">{t("partnership.form.alreadySubmitted", "Դուք արդեն ուղարկել եք գործընկերության հայտը։ Նոր հայտ ուղարկել հնարավոր չէ։")}</p>}
            <button className="partnership-application__button partnership-application__submit" type="submit" disabled={disabled || isSubmitting}>{isSubmitting ? t("partnership.form.submitting") : t("partnership.form.btn")}</button>
        </form>
    );
}

export default function ApplicationSubmissionSection({ openModal }) {
    const { t } = useTranslation();
    const { isAuthenticated } = useAuth();
    const { data: profileResponse } = useProfileQuery(undefined, { skip: !isAuthenticated });
    const user = profileResponse?.data?.user || profileResponse?.user || profileResponse?.data || {};
    const userEmail = user.email || "";
    const [accessToken, setAccessToken] = useState("");
    const [productId, setProductId] = useState(null);
    const [contract, setContract] = useState(null);
    const [loginError, setLoginError] = useState("");
    const [hasExistingApplication, setHasExistingApplication] = useState(false);
    const [isApplicationSubmitted, setIsApplicationSubmitted] = useState(false);
    const [rejectionNote, setRejectionNote] = useState("");
    const copy = (key) => t(`partnership.application.${key}`);

    const refreshApplications = async () => {
        if (!accessToken) return;
        const response = await PartnerApi.get("/api/v1/applications", {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        const { locked, rejectionNote: note } = getApplicationState(extractApplications(response.data));
        setHasExistingApplication(locked);
        setIsApplicationSubmitted(locked);
        setRejectionNote(note);
    };

    const handleContractDownload = async () => {
        if (!contract?.download_url || !accessToken) return;

        const response = await PartnerApi.get(contract.download_url, {
            headers: { Authorization: `Bearer ${accessToken}` },
            responseType: "blob",
        });
        const objectUrl = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = contract.file_name || "payways-contract";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
    };

    useEffect(() => {
        if (!isAuthenticated || !userEmail) return;

        let cancelled = false;
        createSharedLogin(userEmail)
            .then(async (token) => {
                const productsResponse = await PartnerApi.get("/api/v1/products", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const paywaysProduct = findProductBySlug(productsResponse.data, "payways");
                const applicationsResponse = await PartnerApi.get("/api/v1/applications", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const applicationState = getApplicationState(extractApplications(applicationsResponse.data));
                const productDetailsResponse = paywaysProduct?.id
                    ? await PartnerApi.get(`/api/v1/products/${paywaysProduct.id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    })
                    : null;
                const contractData = productDetailsResponse?.data?.data?.contracts?.[0];
                const currentContract = contractData?.current_version
                    ? { ...contractData.current_version, title: contractData.title }
                    : null;

                if (!cancelled) {
                    setAccessToken(token);
                    setProductId(paywaysProduct?.id || paywaysProduct?.product_id || paywaysProduct?.productId || null);
                    setContract(currentContract);
                    setHasExistingApplication(applicationState.locked);
                    setRejectionNote(applicationState.rejectionNote);
                }
            })
            .catch((error) => {
                if (!cancelled) setLoginError(error.response?.data?.message || error.message);
            });

        return () => { cancelled = true; };
    }, [isAuthenticated, userEmail]);

    return (
        <section id="partnership-application" className="partnership-application" aria-labelledby="partnership-application-title">
            {!isAuthenticated ? <div className="partnership-application__guest">
                <h2 id="partnership-application-title">{copy("loginPrompt")}</h2>
                <button className="partnership-application__button" type="button" onClick={() => openModal("login")}>{t("login")}</button>
            </div> : <div className="partnership-application__inner partnership-application__inner--single">
                {/* Գործընկերության պայմանագիր — ժամանակավորապես անջատված է
                <div className="partnership-application__terms">
                    <header className="partnership-application__heading">
                        <h2 id="partnership-application-title">{t("partnership.step1.title")}</h2>
                        <p>{copy("description")}</p>
                        <span className="partnership-application__accent" aria-hidden="true" />
                    </header>
                    <p>{copy("instructions")}</p>
                    <div className="partnership-application__contract">
                        <div className="partnership-application__document">
                            <span className="partnership-application__pdf" aria-hidden="true">
                                <img src={`${assets}pdf-icon.svg`} alt="" />
                            </span>
                            <div><h3 className="partnership-application__contract-title">{contract?.title?.value || t("partnership.step1.documentTitle")}</h3><p>{contract ? `${contract.file_name} · v${contract.version}` : copy("documentMeta")}</p></div>
                        </div>
                        <button className="partnership-application__button partnership-application__download" type="button" onClick={handleContractDownload} disabled={!contract || !accessToken}>
                            <img src={`${assets}download.svg`} alt="" />{t("partnership.step1.downloadBtn")}
                        </button>
                    </div>
                    <div className="partnership-application__notice"><img src={`${assets}info.svg`} alt="" /><p>{copy("notice")}</p></div>
                </div>
                */}
                {loginError && <p className="partnership-application__error" role="alert">{loginError}</p>}
                <ApplicationForm
                    accessToken={accessToken}
                    productId={productId}
                    contract={contract}
                    rejectionNote={rejectionNote}
                    disabled={hasExistingApplication || isApplicationSubmitted}
                        onSuccess={async () => {
                            try {
                                await refreshApplications();
                            } catch {
                                setIsApplicationSubmitted(true);
                            }
                        }}
                />
            </div>}
        </section>
    );
}
